import { useEffect, useState } from "react";
import Chart, { ChartItem, ChartDataset, Plugin } from "chart.js/auto";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Chip,
  Tooltip,
  useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Project } from "../../features/projects/Project";
import { Milestone } from "../../features/projects/Milestone";

/**
 * Canvas plugin that adds a faint glow to selected lines only. A dataset
 * opts in with `glow: true` in its options; everything else draws perfectly
 * sharp. The shadow is kept small with no offset so the line stays crisp —
 * just a subtle halo, not a blur. Cleared after each dataset so points,
 * gridlines and other series are unaffected.
 */
const lineGlowPlugin: Plugin<"line"> = {
  id: "lineGlow",
  beforeDatasetDraw(chart, args) {
    const dataset = chart.data.datasets[args.index] as ChartDataset<"line"> & {
      glow?: boolean;
    };
    if (!dataset?.glow) {
      return;
    }
    const color =
      typeof dataset.borderColor === "string"
        ? dataset.borderColor
        : "rgba(91,141,239,0.6)";
    chart.ctx.shadowColor = color;
    chart.ctx.shadowBlur = 4;
  },
  afterDatasetDraw(chart) {
    chart.ctx.shadowColor = "transparent";
    chart.ctx.shadowBlur = 0;
  },
};

// Series that carry the spotlight glow — kept to the two primary burnup lines
// so the chart reads as crisp with a tasteful accent, not uniformly fuzzy.
const GLOW_SERIES = new Set(["Actual Burnup", "Estimate Burnup"]);

/**
 * Applies the shared crisp line styling to a set of datasets while preserving
 * any per-dataset overrides (e.g. forecast dashes/widths). Tags the primary
 * burnup series so the glow plugin can spotlight them.
 */
const withLineStyle = (
  datasets: ChartDataset<"line">[]
): ChartDataset<"line">[] =>
  datasets.map((d) => ({
    // Base defaults first, then the dataset's own props, then computed
    // props that depend on the dataset (so they win over undefined fields).
    tension: 0.4,
    borderWidth: 2.5,
    pointRadius: 0,
    pointHoverRadius: 5,
    pointHoverBorderWidth: 2,
    pointHoverBorderColor: "#fff",
    pointHitRadius: 12,
    // Don't clip markers at the chart edge — the first/last point's hover dot
    // would otherwise be sliced in half by the chart-area boundary.
    clip: false as const,
    ...d,
    // Solid legend dot (usePointStyle) + filled hover point: mirror the line
    // color into backgroundColor when the dataset hasn't set its own.
    ...(typeof d.borderColor === "string" && d.backgroundColor === undefined
      ? { backgroundColor: d.borderColor }
      : {}),
    ...(d.label && GLOW_SERIES.has(d.label) ? { glow: true } : {}),
  }));

const METRIC_TOOLTIPS: Record<string, string> = {
  "Total estimate":
    "The milestone budget (hours) from [ESTIMATED_HOURS] in the milestone description. This is the number communicated to the client.",
  "Actual burnup":
    "Cumulative actual hours logged in Clockify for this milestone. Shows real work done over time.",
  "Estimate burnup":
    "Projected hours completed based on task completion ratio: Total Estimate x (Completed Tasks / Total Tasks).",
  "Est. Total hours":
    "Legacy formula: Actual Burnup × (Milestone Estimate / Completed Task Estimates). A simple work-proportionality extrapolation that doesn't account for velocity or variance. The Monte Carlo forecast (blue lines on the chart) uses historical overrun multipliers from closed tasks to project remaining hours with P50/P80/P95 confidence bands.",
  "Total Task Estimate":
    "Sum of individual task time estimates (hours) for all tasks created up to each date.",
  "Total Of Est. Completed":
    "Sum of time estimates (hours) for tasks that have been closed/completed.",
  "Total Tasks":
    "Count of tasks created in this milestone up to each date.",
  "Gain From Estimate":
    "Estimate Burnup minus Actual Burnup. Positive (green) means you're ahead of the estimate; negative (red) means behind.",
  "Actual / Total Task Est.":
    "Ratio of actual hours worked to total task estimates. Below 1.0 = under budget per task, above 1.0 = over budget.",
  "Actual / Total Estimate":
    "Ratio of actual hours worked to the milestone estimate. Below 1.0 = under the client budget, above 1.0 = over budget.",
  "Actual / Estimate Burnup":
    "Ratio of actual hours to estimate burnup. Below 1.0 = completing tasks with less effort than estimated.",
};

interface ProjectProgressReportProps {
  projects: Project[];
  selectedProject: Project;
  selectedMilestone: Milestone;
  handleProjectChange?: (event: any) => void;
  handleMilestoneChange?: (event: any) => void;
}

const TaskProgressStrip = ({ total, open }: { total: number; open: number }) => {
  const theme = useTheme();
  if (total <= 0) {
    return null;
  }
  const closed = Math.max(0, total - open);
  const segments = Array.from({ length: total }, (_, i) => i < closed);
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          mb: 0.75,
        }}
      >
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>
          Task completion
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {closed} / {total} closed · {open} open
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: "3px", width: "100%" }}>
        {segments.map((isClosed, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              height: "6px",
              borderRadius: "2px",
              backgroundColor: isClosed
                ? "rgba(75, 192, 192, 1)"
                : theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.12)"
                : "rgba(0, 0, 0, 0.08)",
              transition: "background-color 0.2s",
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

const buildLegacyEstimateDataset = (selectedMilestone: Milestone) => {
  if (selectedMilestone?.forecast) {
    return [];
  }
  return [
    {
      label: "Est. Total Hours (ratio)",
      tension: 0.4,
      data:
        selectedMilestone?.estimatedTotalHoursNeeded?.map((item) => item.value) ||
        [],
      borderColor: "rgba(153, 102, 255, 1)",
      fill: false,
    },
  ];
};

const buildForecastDatasets = (selectedMilestone: Milestone, pointCount: number) => {
  const forecast = selectedMilestone?.forecast;
  if (!forecast || pointCount === 0) {
    return [];
  }

  const history = selectedMilestone?.forecastHistory ?? [];
  const dayKey = (d: string | Date) => {
    const x = new Date(d);
    return Date.UTC(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate());
  };
  const chartDays = (selectedMilestone?.totalEstimate ?? []).map((p) =>
    dayKey(p.date)
  );

  // One history point → flat horizontal reference line at that value.
  // Multiple history points → plot per chart date via carry-forward, matching
  // by calendar day so precise forecast timestamps align with daily chart
  // slots. A forecast snapshot taken later in the day still counts for that
  // day. Forecasts past the last chart day land on the final slot so the
  // latest value is always visible.
  const seriesFor = (
    selector: (h: (typeof history)[number]) => number,
    fallback: number
  ): (number | null)[] => {
    if (history.length <= 1 || chartDays.length === 0) {
      return new Array(pointCount).fill(fallback);
    }
    const sorted = [...history].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    const values: (number | null)[] = new Array(pointCount).fill(null);
    const lastDay = chartDays[chartDays.length - 1];
    for (const h of sorted) {
      const hDay = dayKey(h.date);
      let slot = chartDays.findIndex((d) => d >= hDay);
      if (slot === -1) {
        if (hDay >= lastDay) slot = chartDays.length - 1;
        else continue;
      }
      values[slot] = selector(h);
    }
    const firstKnown = values.find((v) => v !== null) ?? null;
    let current: number | null = firstKnown;
    for (let i = 0; i < values.length; i++) {
      if (values[i] !== null) current = values[i];
      else values[i] = current;
    }
    return values;
  };

  const mcLine = (
    data: (number | null)[],
    label: string,
    color: string,
    dash: number[]
  ) => ({
    label,
    data,
    borderColor: color,
    borderWidth: 2,
    borderDash: dash,
    pointRadius: 3,
    fill: false,
    tension: 0.2,
    spanGaps: false,
  });

  return [
    mcLine(
      seriesFor((h) => h.p50TotalHours, forecast.p50TotalHours),
      `MC P50 (${forecast.p50TotalHours.toFixed(0)}h)`,
      "rgba(108, 120, 240, 0.95)",
      []
    ),
    mcLine(
      seriesFor((h) => h.p80TotalHours, forecast.p80TotalHours),
      `MC P80 (${forecast.p80TotalHours.toFixed(0)}h)`,
      "rgba(108, 120, 240, 0.6)",
      [6, 4]
    ),
    mcLine(
      seriesFor((h) => h.p95TotalHours, forecast.p95TotalHours),
      `MC P95 (${forecast.p95TotalHours.toFixed(0)}h)`,
      "rgba(108, 120, 240, 0.35)",
      [2, 4]
    ),
  ];
};

const ProjectProgressReport = ({
  selectedProject,
  selectedMilestone,
}: ProjectProgressReportProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [chart, setChart] = useState<Chart | null>(null);

  // Chart.js draws on a canvas and can't read the MUI theme, so feed it
  // mode-aware grid / tick / tooltip colors explicitly.
  const chartGridColor = isDark
    ? "rgba(255, 255, 255, 0.08)"
    : "rgba(0, 0, 0, 0.05)";
  const chartTickColor = isDark
    ? "rgba(230, 236, 245, 0.7)"
    : "rgba(0, 0, 0, 0.6)";
  const chartTooltipBg = isDark
    ? "rgba(20, 26, 38, 0.92)"
    : "rgba(0, 0, 0, 0.8)";

  useEffect(() => {
    if (!selectedMilestone) {
      return () => {};
    }

    const chartItem = document.getElementById("chart-metrics") as ChartItem;
    const labelCount = selectedMilestone?.totalEstimate?.length ?? 0;

    if (chart != null) {
      chart.data.labels =
        selectedMilestone?.totalEstimate?.map((item) =>
          new Date(item.date).toLocaleDateString()
        ) || [];
      chart.data.datasets = withLineStyle([
        {
          label: "Total Estimate",
          data:
            selectedMilestone?.totalEstimate?.map((item) => item.value) || [],
          borderColor: "rgba(75, 192, 192, 1)",
          fill: false,
        },
        {
          label: "Actual Burnup",
          data:
            selectedMilestone?.actualBurnUp?.map((item) => item.value) || [],
          borderColor: "rgba(255, 99, 132, 1)",
          fill: false,
        },
        {
          label: "Estimate Burnup",
          data:
            selectedMilestone?.estimateBurnUp?.map((item) => item.value) || [],
          borderColor: "rgba(255, 159, 64, 1)",
          fill: false,
        },
        ...buildLegacyEstimateDataset(selectedMilestone),
        {
          label: "Total Task Estimate",
          data:
            selectedMilestone?.totalTaskEstimates?.map((item) => item.value) ||
            [],
          borderColor: "rgba(54, 162, 235, 1)",
          fill: false,
        },
        {
          label: "Total Of Estimated Completed",
          data:
            selectedMilestone?.totalOfEstimatedCompleted?.map(
              (item) => item.value
            ) || [],
          borderColor: "rgba(255, 105, 180, 1)",
          fill: false,
        },
        {
          label: "Total Tasks",
          data: selectedMilestone?.totalTasks?.map((item) => item.value) || [],
          borderColor: "rgba(201, 203, 207, 1)",
          fill: false,
        },
        ...buildForecastDatasets(selectedMilestone, labelCount),
      ]);
      // Recolor for the active theme mode (handles light <-> dark switches).
      if (chart.options.scales?.y?.grid) {
        chart.options.scales.y.grid.color = chartGridColor;
      }
      if (chart.options.scales?.y?.ticks) {
        chart.options.scales.y.ticks.color = chartTickColor;
      }
      if (chart.options.scales?.x?.ticks) {
        chart.options.scales.x.ticks.color = chartTickColor;
      }
      if (chart.options.plugins?.legend?.labels) {
        chart.options.plugins.legend.labels.color = chartTickColor;
      }
      if (chart.options.plugins?.tooltip) {
        chart.options.plugins.tooltip.backgroundColor = chartTooltipBg;
      }
      chart.update();
    } else {
      const newChart = new Chart(chartItem, {
        type: "line",
        plugins: [lineGlowPlugin],
        data: {
          labels: selectedMilestone.totalEstimate.map((item) =>
            new Date(item.date).toLocaleDateString()
          ),
          datasets: withLineStyle([
            {
              label: "Total Estimate",
              data: selectedMilestone.totalEstimate.map((item) => item.value),
              borderColor: "rgba(75, 192, 192, 1)",
              fill: false,
            },
            {
              label: "Actual Burnup",
              data: selectedMilestone.actualBurnUp.map((item) => item.value),
              borderColor: "rgba(255, 99, 132, 1)",
              fill: false,
            },
            {
              label: "Estimate Burnup",
              data: selectedMilestone.estimateBurnUp.map((item) => item.value),
              borderColor: "rgba(255, 159, 64, 1)",
              fill: false,
            },
            ...buildLegacyEstimateDataset(selectedMilestone),
            {
              label: "Total Task Estimate",
              data: selectedMilestone.totalTaskEstimates.map(
                (item) => item.value
              ),
              borderColor: "rgba(54, 162, 235, 1)",
              fill: false,
            },
            {
              label: "Total Of Estimated Completed",
              data: selectedMilestone.totalOfEstimatedCompleted.map(
                (item) => item.value
              ),
              borderColor: "rgba(255, 105, 180, 1)",
              fill: false,
            },
            {
              label: "Total Tasks",
              data: selectedMilestone.totalTasks.map((item) => item.value),
              borderColor: "rgba(201, 203, 207, 1)",
              fill: false,
            },
            ...buildForecastDatasets(selectedMilestone, labelCount),
          ]),
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          // Breathing room so edge points (and their hover markers) aren't
          // clipped by the canvas edge.
          layout: {
            padding: { left: 4, right: 8, top: 8, bottom: 0 },
          },
          animation: {
            duration: 900,
            easing: "easeOutQuart",
          },
          animations: {
            // Lines ease up from the baseline on load / milestone change.
            y: {
              from: (ctx) =>
                ctx.type === "data" && ctx.mode === "default"
                  ? ctx.chart.scales.y.getPixelForValue(0)
                  : undefined,
            },
          },
          interaction: {
            mode: "index",
            intersect: false,
          },
          plugins: {
            legend: {
              display: true,
              position: 'bottom',
              align: "center",
              labels: {
                usePointStyle: true,
                pointStyle: "circle",
                boxWidth: 8,
                boxHeight: 8,
                padding: 18,
                color: chartTickColor,
                font: {
                  size: 12,
                  weight: 600,
                },
              },
            },
            tooltip: {
              backgroundColor: chartTooltipBg,
              padding: 12,
              titleFont: {
                size: 13,
              },
              bodyFont: {
                size: 12,
              },
              cornerRadius: 10,
              displayColors: true,
              usePointStyle: true,
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: {
                color: chartGridColor,
              },
              ticks: {
                color: chartTickColor,
              },
            },
            x: {
              grid: {
                display: false,
              },
              ticks: {
                color: chartTickColor,
              },
            },
          },
        },
      });
      setChart(newChart);
    }

    return () => {};
  }, [selectedMilestone, chart, chartGridColor, chartTickColor, chartTooltipBg]);

  useEffect(() => {
    return () => {
      if (chart) {
        chart.destroy();
      }
    };
  }, [chart]);

  const ratio =
    (selectedMilestone?.actualBurnUp?.[
      (selectedMilestone?.actualBurnUp?.length || 1) - 1
    ]?.value || 0) /
    (selectedMilestone?.totalTaskEstimates?.[
      (selectedMilestone?.totalTaskEstimates?.length || 1) - 1
    ]?.value || 1);

  const actualToTotalEstimateRatio =
    (selectedMilestone?.actualBurnUp?.[
      (selectedMilestone?.actualBurnUp?.length || 1) - 1
    ]?.value || 0) /
    (selectedMilestone?.totalEstimate?.[
      (selectedMilestone?.totalEstimate?.length || 1) - 1
    ]?.value || 1);

  const actualToEstimateBurnupRatio =
    (selectedMilestone?.actualBurnUp?.[
      (selectedMilestone?.actualBurnUp?.length || 1) - 1
    ]?.value || 0) /
    (selectedMilestone?.estimateBurnUp?.[
      (selectedMilestone?.estimateBurnUp?.length || 1) - 1
    ]?.value || 1);

  let color =
    (ratio <= 1 && "rgba(75, 192, 192, 1)") ||
    (ratio > 1 && ratio <= 1.2 && "rgba(255, 159, 64, 1)") ||
    "rgba(255, 99, 132, 1)";

  let actualToTotalEstimateColor =
    (actualToTotalEstimateRatio <= 1 && "rgba(75, 192, 192, 1)") ||
    (actualToTotalEstimateRatio > 1 &&
      actualToTotalEstimateRatio <= 1.2 &&
      "rgba(255, 159, 64, 1)") ||
    "rgba(255, 99, 132, 1)";

  let actualToEstimateBurnupColor =
    (actualToEstimateBurnupRatio <= 1 && "rgba(75, 192, 192, 1)") ||
    (actualToEstimateBurnupRatio > 1 &&
      actualToEstimateBurnupRatio <= 1.2 &&
      "rgba(255, 159, 64, 1)") ||
    "rgba(255, 99, 132, 1)";

  const lastKnownDate =
    selectedMilestone?.totalEstimate?.[
      (selectedMilestone?.totalEstimate?.length || 1) - 1
    ]?.date;

  const formattedLastKnownDate = lastKnownDate
    ? new Date(lastKnownDate).toLocaleString()
    : "No data available";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        mt: 3,
      }}
    >
      {/* Chart Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          animation: "trayFadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) both",
          "@keyframes trayFadeUp": {
            from: { opacity: 0, transform: "translateY(12px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: { xs: "350px", sm: "400px", md: "450px" },
            position: "relative",
          }}
        >
          <Box
            component="canvas"
            id="chart-metrics"
            sx={{
              height: {
                xs: "350px !important",
                sm: "400px !important",
                md: "450px !important",
              },
            }}
          />
        </Box>
      </Paper>

      {/* Metrics Cards Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", lg: "1fr 1fr 1fr" },
          gap: 2,
        }}
      >
        {/* Latest Values Card */}
        <Card elevation={0}>
          <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Latest Values
            </Typography>
            <TaskProgressStrip
              total={selectedMilestone?.totalTaskCount ?? 0}
              open={selectedMilestone?.openTaskCount ?? 0}
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderLeft: "3px solid rgba(75, 192, 192, 1)",
                  pl: 1.5,
                  backgroundColor: "rgba(75, 192, 192, 0.05)",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Total estimate
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Total estimate"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Typography variant="body1" fontWeight={600}>
                  {selectedMilestone
                    ? (
                        selectedMilestone.totalEstimate[
                          (selectedMilestone.totalEstimate.length || 1) - 1
                        ]?.value || 0
                      ).toFixed(2)
                    : "N/A"}
                  h
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderLeft: "3px solid rgba(255, 99, 132, 1)",
                  pl: 1.5,
                  backgroundColor: "rgba(255, 99, 132, 0.05)",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Actual burnup
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Actual burnup"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Typography variant="body1" fontWeight={600}>
                  {(
                    selectedMilestone?.actualBurnUp?.[
                      (selectedMilestone?.actualBurnUp?.length || 1) - 1
                    ]?.value || 0
                  ).toFixed(2)}
                  h
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderLeft: "3px solid rgba(255, 159, 64, 1)",
                  pl: 1.5,
                  backgroundColor: "rgba(255, 159, 64, 0.05)",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Estimate burnup
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Estimate burnup"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Typography variant="body1" fontWeight={600}>
                  {(
                    selectedMilestone?.estimateBurnUp?.[
                      (selectedMilestone?.estimateBurnUp?.length || 1) - 1
                    ]?.value || 0
                  ).toFixed(2)}
                  h
                </Typography>
              </Box>

              {!selectedMilestone?.forecast && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    borderLeft: "3px solid rgba(153, 102, 255, 1)",
                    pl: 1.5,
                    backgroundColor: "rgba(153, 102, 255, 0.05)",
                    borderRadius: "0 4px 4px 0",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="body2" fontWeight={500}>
                      Est. Total hours (ratio)
                    </Typography>
                    <Tooltip title={METRIC_TOOLTIPS["Est. Total hours"]} arrow>
                      <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                    </Tooltip>
                  </Box>
                  <Typography variant="body1" fontWeight={600}>
                    {(
                      selectedMilestone?.estimatedTotalHoursNeeded?.[
                        (selectedMilestone?.estimatedTotalHoursNeeded?.length || 1) -
                          1
                      ]?.value || 0
                    ).toFixed(2)}
                    h
                  </Typography>
                </Box>
              )}

              {selectedMilestone?.forecast && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    py: 1,
                    borderLeft: "3px solid rgba(108, 120, 240, 1)",
                    pl: 1.5,
                    backgroundColor: "rgba(108, 120, 240, 0.06)",
                    borderRadius: "0 4px 4px 0",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>
                        Monte Carlo forecast (total hours)
                      </Typography>
                      <Tooltip
                        title={`Overrun-multiplier model: for each closed task, compute actual/estimate ratio; sample those to project remaining estimated hours. ${selectedMilestone.forecast.overrunSamplesUsed} overrun samples, ${selectedMilestone.forecast.velocitySamplesUsed} velocity samples. Mean overrun: ${selectedMilestone.forecast.overrunMultiplierMean.toFixed(2)}× (±${selectedMilestone.forecast.overrunMultiplierStdDev.toFixed(2)}).`}
                        arrow
                      >
                        <InfoOutlinedIcon
                          sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }}
                        />
                      </Tooltip>
                    </Box>
                    <Typography variant="body1" fontWeight={700}>
                      {selectedMilestone.forecast.p50TotalHours.toFixed(1)}h
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ color: "text.secondary", ml: 0.5 }}
                      >
                        (P50)
                      </Typography>
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    <span>
                      P80 {selectedMilestone.forecast.p80TotalHours.toFixed(1)}h ·
                      P95 {selectedMilestone.forecast.p95TotalHours.toFixed(1)}h
                    </span>
                    <span>
                      done {selectedMilestone.forecast.actualHoursSoFar.toFixed(0)}h
                      / remaining est. {selectedMilestone.forecast.remainingEstimateHours.toFixed(0)}h
                    </span>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    <span>
                      ETA P50 {new Date(selectedMilestone.forecast.p50Date).toLocaleDateString()} ·
                      P80 {new Date(selectedMilestone.forecast.p80Date).toLocaleDateString()}
                    </span>
                    <span>
                      vs plan {selectedMilestone.forecast.sprintTotalEstimateHours.toFixed(0)}h
                    </span>
                  </Box>
                </Box>
              )}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderLeft: "3px solid rgba(54, 162, 235, 1)",
                  pl: 1.5,
                  backgroundColor: "rgba(54, 162, 235, 0.05)",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Total Task Estimate
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Total Task Estimate"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Typography variant="body1" fontWeight={600}>
                  {(
                    selectedMilestone?.totalTaskEstimates?.[
                      (selectedMilestone?.totalTaskEstimates?.length || 1) - 1
                    ]?.value || 0
                  ).toFixed(2)}
                  h
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderLeft: "3px solid rgba(255, 105, 180, 1)",
                  pl: 1.5,
                  backgroundColor: "rgba(255, 105, 180, 0.05)",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Total Of Est. Completed
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Total Of Est. Completed"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Typography variant="body1" fontWeight={600}>
                  {(
                    selectedMilestone?.totalOfEstimatedCompleted?.[
                      (selectedMilestone?.totalOfEstimatedCompleted?.length || 1) -
                        1
                    ]?.value || 0
                  ).toFixed(2)}
                  h
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderLeft: "3px solid rgba(201, 203, 207, 1)",
                  pl: 1.5,
                  backgroundColor: "rgba(201, 203, 207, 0.05)",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Total Tasks
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Total Tasks"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Typography variant="body1" fontWeight={600}>
                  {(
                    selectedMilestone?.totalTasks?.[
                      (selectedMilestone?.totalTasks?.length || 1) - 1
                    ]?.value || 0
                  ).toFixed(0)}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderLeft: "3px solid rgba(158, 158, 158, 1)",
                  pl: 1.5,
                  backgroundColor: "rgba(158, 158, 158, 0.05)",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Open / Total tasks
                  </Typography>
                  <Tooltip
                    title="Tasks without a GitLab/GitHub close date vs. all tasks imported for this milestone. Updated on every sync."
                    arrow
                  >
                    <InfoOutlinedIcon
                      sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }}
                    />
                  </Tooltip>
                </Box>
                <Typography variant="body1" fontWeight={600}>
                  {selectedMilestone?.openTaskCount ?? 0}
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ color: "text.secondary", mx: 0.5 }}
                  >
                    /
                  </Typography>
                  {selectedMilestone?.totalTaskCount ?? 0}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderLeft: "3px solid rgba(0, 0, 133, 1)",
                  pl: 1.5,
                  backgroundColor: "rgba(0, 0, 133, 0.05)",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Gain From Estimate
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Gain From Estimate"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Chip
                  label={
                    (
                      (selectedMilestone?.estimateBurnUp?.[
                        (selectedMilestone?.estimateBurnUp?.length || 1) - 1
                      ]?.value || 0) -
                      (selectedMilestone?.actualBurnUp?.[
                        (selectedMilestone?.actualBurnUp?.length || 1) - 1
                      ]?.value || 0)
                    ).toFixed(2) === "0.00"
                      ? "0h"
                      : `${(
                          (selectedMilestone?.estimateBurnUp?.[
                            (selectedMilestone?.estimateBurnUp?.length || 1) - 1
                          ]?.value || 0) -
                          (selectedMilestone?.actualBurnUp?.[
                            (selectedMilestone?.actualBurnUp?.length || 1) - 1
                          ]?.value || 0)
                        ).toFixed(2)}h`
                  }
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor:
                      (selectedMilestone?.estimateBurnUp?.[
                        (selectedMilestone?.estimateBurnUp?.length || 1) - 1
                      ]?.value || 0) -
                        (selectedMilestone?.actualBurnUp?.[
                          (selectedMilestone?.actualBurnUp?.length || 1) - 1
                        ]?.value || 0) ===
                      0
                        ? "rgba(75, 192, 192, 0.15)"
                        : (selectedMilestone?.estimateBurnUp?.[
                            (selectedMilestone?.estimateBurnUp?.length || 1) - 1
                          ]?.value || 0) -
                            (selectedMilestone?.actualBurnUp?.[
                              (selectedMilestone?.actualBurnUp?.length || 1) - 1
                            ]?.value || 0) >
                          0
                        ? "rgba(75, 192, 192, 0.15)"
                        : "rgba(255, 99, 132, 0.15)",
                    color:
                      (selectedMilestone?.estimateBurnUp?.[
                        (selectedMilestone?.estimateBurnUp?.length || 1) - 1
                      ]?.value || 0) -
                        (selectedMilestone?.actualBurnUp?.[
                          (selectedMilestone?.actualBurnUp?.length || 1) - 1
                        ]?.value || 0) >=
                      0
                        ? "rgba(75, 192, 192, 1)"
                        : "rgba(255, 99, 132, 1)",
                  }}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Ratios Card */}
        <Card elevation={0}>
          <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
            <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 600 }}>
              Ratios
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1.5,
                  backgroundColor: "rgba(0, 0, 0, 0.02)",
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Actual / Total Task Est.
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Actual / Total Task Est."]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Chip
                  label={ratio.toFixed(2)}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor:
                      ratio <= 1
                        ? "rgba(75, 192, 192, 0.15)"
                        : ratio <= 1.2
                        ? "rgba(255, 159, 64, 0.15)"
                        : "rgba(255, 99, 132, 0.15)",
                    color: color,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1.5,
                  backgroundColor: "rgba(0, 0, 0, 0.02)",
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Actual / Total Estimate
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Actual / Total Estimate"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Chip
                  label={actualToTotalEstimateRatio.toFixed(2)}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor:
                      actualToTotalEstimateRatio <= 1
                        ? "rgba(75, 192, 192, 0.15)"
                        : actualToTotalEstimateRatio <= 1.2
                        ? "rgba(255, 159, 64, 0.15)"
                        : "rgba(255, 99, 132, 0.15)",
                    color: actualToTotalEstimateColor,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1.5,
                  backgroundColor: "rgba(0, 0, 0, 0.02)",
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Actual / Estimate Burnup
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Actual / Estimate Burnup"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Chip
                  label={actualToEstimateBurnupRatio.toFixed(2)}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor:
                      actualToEstimateBurnupRatio <= 1
                        ? "rgba(75, 192, 192, 0.15)"
                        : actualToEstimateBurnupRatio <= 1.2
                        ? "rgba(255, 159, 64, 0.15)"
                        : "rgba(255, 99, 132, 0.15)",
                    color: actualToEstimateBurnupColor,
                  }}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Last Known Date Card */}
        <Card elevation={0}>
          <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
            <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 600 }}>
              Last Known Date
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Date and time stamp:
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {formattedLastKnownDate}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ProjectProgressReport;
